import { useEffect, useRef, useState } from 'react'
import { ImageCropper } from './ImageCropper'
import { Banner, Button } from '@shop/ui'
import { useAuthedImage } from './useAuthedImage'

/** ما يُقبل رفعُه — والخادمُ يفحص البايتات، وهذا تسهيلٌ للاختيار لا حارس. */
const ACCEPT = 'image/jpeg,image/png,image/webp'

/** ثمانيةُ ميجا — الحدُّ نفسُه في `MediaStore.MaxBytes`. */
const MAX_BYTES = 8 * 1024 * 1024

/**
 * حقلُ صورةٍ واحدة: يعرض الموجودةَ، ويلتقط بديلًا، وينزع.
 *
 * <b>ويعمل في حالتين</b>: صورةٌ محفوظةٌ على الخادم (`path` معطى)، أو صورةٌ
 * اختيرت ولم تُرفع بعد (`onPick` — لأنّ شاشة الإدخال تختار الصورةَ **قبل** أن
 * تُنشأ المسوّدة، فلا مكانَ ترفع إليه بعد).
 */
export function ImageField({
  path,
  onPick,
  onRemove,
  busy = false,
  readOnly = false,
  label = 'صورةُ المادّة',
  hint,
}: {
  /** مسارُ الصورة على الخادم، أو `null` إن لم تكن هناك صورةٌ محفوظة. */
  path: string | null
  /** يُنادى بالملفّ المختار. الرفعُ مسؤوليّةُ الشاشة — هي التي تعرف متى. */
  onPick?: (file: File) => void
  /** يُنادى لنزع الصورة المحفوظة. غيابُه يُخفي زرَّ النزع. */
  onRemove?: () => void
  busy?: boolean
  readOnly?: boolean
  label?: string
  hint?: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const [local, setLocal] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // ما اختير ولم يُقصّ بعد. والقصُّ يمرّ به **كلُّ** اختيار — في الإدخال
  // وفي المراجعة وفي شاشة الصنف وفي الإدخال المباشر — لأنّ هذا المكوّنَ واحدٌ
  // في الأربع. فتُدوَّر الصورةُ المقلوبةُ وتُقصَّ الخلفيّةُ قبل أن تصعد.
  const [editing, setEditing] = useState<File | null>(null)

  const remote = useAuthedImage(path)

  // معاينةُ ما اختير قبل رفعه — وتُبطَل كما تُبطَل المجلوبة.
  useEffect(() => () => {
    if (local !== null) {
      URL.revokeObjectURL(local)
    }
  }, [local])

  const pick = (file: File | undefined) => {
    if (!file) {
      return
    }

    if (file.size > MAX_BYTES) {
      setError(`الصورةُ ${(file.size / (1024 * 1024)).toFixed(1)} ميجا — والحدُّ ثمانية.`)

      return
    }

    setError(null)
    setEditing(file)
  }

  /** ما خرج من القاصّ: هو ما يُعرض وهو ما يُرفع. */
  const accept = (ready: File) => {
    setEditing(null)
    setLocal((current) => {
      if (current !== null) {
        URL.revokeObjectURL(current)
      }

      return URL.createObjectURL(ready)
    })

    onPick?.(ready)
  }

  const shown = local ?? remote.url

  return (
    <div className="space-y-2">
      <span className="block text-xs font-medium text-ink-soft">{label}</span>

      <div className="flex items-start gap-3">
        <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border border-line bg-surface">
          {remote.loading && shown === null
            ? <span className="text-xs text-ink-soft">يُحمَّل…</span>
            : shown === null
              ? <span className="text-xs text-ink-soft">لا صورة</span>
              : <img src={shown} alt={label} className="h-full w-full object-contain" />}
        </div>

        {!readOnly && (
          <div className="space-y-2">
            {/* ⚠ `capture="environment"` تفتح الكاميرا الخلفيّة على الهاتف
                مباشرةً، وتُهمَل على سطح المكتب فيُفتح منتقي الملفّات. زرٌّ واحدٌ
                يخدم الحالتين — ومن يُدخل مئةَ صنفٍ يصوّر بيدٍ ويكتب بالأخرى. */}
            <input
              ref={input}
              type="file"
              accept={ACCEPT}
              capture="environment"
              className="hidden"
              onChange={(event) => {
                pick(event.target.files?.[0])

                // يُفرَّغ ليُقبل اختيارُ الملفّ نفسِه مرّةً ثانية بعد نزعه.
                event.target.value = ''
              }}
            />

            <Button onClick={() => input.current?.click()} disabled={busy}>
              {shown === null ? 'التقط صورةً' : 'استبدلها'}
            </Button>

            {shown !== null && onRemove && (
              <Button
                onClick={() => {
                  setLocal((current) => {
                    if (current !== null) {
                      URL.revokeObjectURL(current)
                    }

                    return null
                  })
                  onRemove()
                }}
                disabled={busy}
              >
                انزعها
              </Button>
            )}

            {hint && <p className="max-w-xs text-xs text-ink-soft">{hint}</p>}
          </div>
        )}
      </div>

      {editing !== null && (
        <ImageCropper
          file={editing}
          onDone={accept}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* ⚠ `Banner` لا `ErrorBanner`: الثاني لا يعرض نصَّ ما ليس `ApiError`،
          فرسالةُ حدِّ الحجم هذه كانت ستظهر «تعذّر الاتّصالُ بالخادم». */}
      {error && <Banner tone="danger">{error}</Banner>}
    </div>
  )
}
